import removeMarkdown from 'remove-markdown';

import getWords from './getWords';
import type { TruncationResult } from './truncateText';

interface OpenMarker {
  marker: string;
  position: number;
}

export function truncateMarkdown(
  markdown: string,
  maxWords: number,
  language?: string,
): TruncationResult {
  if (!markdown.trim()) {
    return { text: markdown, wasTruncated: false, originalWordCount: 0, truncatedWordCount: 0 };
  }

  const textOnly = removeMarkdown(markdown);
  const allWords = getWords(textOnly, language ?? 'unknown');
  const originalWordCount = allWords.length;

  if (originalWordCount <= maxWords) {
    return { text: markdown, wasTruncated: false, originalWordCount, truncatedWordCount: originalWordCount };
  }

  const { truncated, wordCount } = truncateMarkdownContent(markdown, maxWords, language);

  return {
    text: truncated,
    wasTruncated: true,
    originalWordCount,
    truncatedWordCount: wordCount,
  };
}

function countWords(text: string, language: string): number {
  return getWords(removeMarkdown(text), language).length;
}

function findWordEndPosition(text: string, targetWordCount: number, language: string): number {
  const stripped = removeMarkdown(text);
  const words = getWords(stripped, language);

  if (targetWordCount >= words.length) {
    return text.length;
  }

  const targetWords = words.slice(0, targetWordCount);
  let strippedPos = 0;
  let textPos = 0;

  for (const word of targetWords) {
    const wordPosInStripped = stripped.indexOf(word, strippedPos);
    if (wordPosInStripped === -1) {
      break;
    }

    const charsToFind = wordPosInStripped - strippedPos + word.length;
    let charsFound = 0;

    while (textPos < text.length && charsFound < charsToFind) {
      const char = text[textPos];
      const isMarkdown = /[*_`~#>[\]()!-]/.test(char) && !stripped.includes(text.slice(textPos, textPos + 1));
      if (!isMarkdown || stripped[strippedPos + charsFound] === char) {
        charsFound++;
      }
      textPos++;
    }
    strippedPos = wordPosInStripped + word.length;
  }

  return textPos;
}

function truncateMarkdownContent(
  markdown: string,
  maxWords: number,
  language?: string,
): { truncated: string; wordCount: number; } {
  const openMarkers: OpenMarker[] = [];
  let result = '';
  let i = 0;
  let lastWordBoundary = 0;
  let lastWordBoundaryMarkers: OpenMarker[] = [];
  const lang = language ?? 'unknown';

  const markers = ['***', '___', '**', '__', '~~', '*', '_', '`'];

  while (i < markdown.length) {
    const currentWordCount = countWords(result, lang);
    if (currentWordCount >= maxWords) {
      if (lastWordBoundary === 0) {
        lastWordBoundary = findWordEndPosition(result, maxWords - 1, lang);
        lastWordBoundaryMarkers = [];
        for (const marker of openMarkers) {
          if (marker.position < lastWordBoundary) {
            lastWordBoundaryMarkers.push({ ...marker });
          }
        }
      }
      result = result.slice(0, lastWordBoundary);
      openMarkers.length = 0;
      openMarkers.push(...lastWordBoundaryMarkers);
      break;
    }

    if (/\s/.test(markdown[i])) {
      lastWordBoundary = result.length + 1;
      lastWordBoundaryMarkers = openMarkers.map((m) => ({ ...m }));
    }

    let foundMarker = false;
    for (const marker of markers) {
      if (markdown.slice(i, i + marker.length) === marker) {
        const existingIndex = openMarkers.findIndex((m) => m.marker === marker);
        if (existingIndex >= 0) {
          openMarkers.splice(existingIndex, 1);
        } else {
          openMarkers.push({ marker, position: result.length });
        }
        result += marker;
        i += marker.length;
        foundMarker = true;
        break;
      }
    }

    if (foundMarker) {
      continue;
    }

    if (markdown[i] === '[') {
      const linkMatch = markdown.slice(i).match(/^\[([^\]]*)\]\(([^)]*)\)/);
      if (linkMatch) {
        const linkText = linkMatch[1];
        const linkUrl = linkMatch[2];
        const linkWords = getWords(linkText, lang);
        const currentCount = countWords(result, lang);

        if (currentCount + linkWords.length <= maxWords) {
          result += linkMatch[0];
          i += linkMatch[0].length;
          continue;
        }
        const remainingWords = maxWords - currentCount;
        if (remainingWords > 0) {
          const truncatedLinkText = linkWords.slice(0, remainingWords).join(' ');
          result += `[${truncatedLinkText}](${linkUrl})`;
        }
        break;
      }
    }

    result += markdown[i];
    i++;
  }

  const wordCount = countWords(result, lang);

  for (let j = openMarkers.length - 1; j >= 0; j--) {
    result += openMarkers[j].marker;
  }

  return { truncated: result.trim(), wordCount };
}
